import { trpc } from '@org/clients/school';
import {
  DynamicForm,
  StateSelectField,
  unwrapTrpcData,
  type StateOption,
} from '@org/ui';
import { personUpdateSchema } from '@org/model';
import { toast } from 'sonner';
import { useState, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { z } from 'zod/v4';

type PersonFormValues = z.infer<typeof personUpdateSchema>;

type StateRendererContext = {
  field: ControllerRenderProps<PersonFormValues>;
  methods: UseFormReturn<PersonFormValues>;
};

export interface PersonFormProps {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
  hideButtons?: boolean;
  /** Edits vs. the loaded values — lets the multi-step shell warn before leaving the step. */
  onDirtyChange?: (dirty: boolean) => void;
}

export interface PersonFormRef {
  submit: () => void;
}

export const PersonForm = forwardRef<PersonFormRef, PersonFormProps>(({
  defaultValues,
  onSubmit,
  onClose,
  hideButtons,
  onDirtyChange,
}, ref) => {
  const utils = trpc.useUtils();
  const personId = defaultValues?.personId;
  const userId = defaultValues?.userId;
  const { t } = useTranslation('admin');

  const [formDefaultValues, setFormDefaultValues] = useState(defaultValues);
  const formRef = useRef<HTMLFormElement>(null);

  const { data: countries } = trpc.countries.list.useQuery();
  const { data: states } = trpc.states.list.useQuery();

  const countryOptions = useMemo(
    () => unwrapTrpcData(countries) ?? [],
    [countries]
  );

  const stateOptions = useMemo(
    () => (unwrapTrpcData(states) ?? []) as StateOption[],
    [states]
  );

  const createMutation = trpc.persons.create.useMutation({
    onSuccess: () => {
      utils.persons.getByUserId.invalidate();
      toast.success(t('common.toasts.createSuccess', { entity: 'Person details' }));
    }
  });

  const updateMutation = trpc.persons.update.useMutation({
    onSuccess: () => {
      utils.persons.getByUserId.invalidate();
      toast.success(t('common.toasts.updateSuccess', { entity: 'Person details' }));
    }
  });

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (formRef.current) {
        // @ts-expect-error - dispatchEvent is not typed correctly
        formRef.current.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }));

  const handleFormSubmit = async (data: any) => {
    try {
      let resultData = data;

      if (personId) {
        await updateMutation.mutateAsync({
          id: personId,
          data: {
            userId,
            address: data.address,
            city: data.city,
            zipCode: data.zipCode,
            stateId: data.stateId,
            countryId: data.countryId,
            bio: data.bio,
            profession: data.profession,
            dateOfBirth: data.dateOfBirth,
            nationality: data.nationality,
            socialLinks: data.socialLinks,
            educationLevel: data.educationLevel,
            leveling: data.leveling,
          }
        });
        resultData = { ...data, personId };
      } else {
        const created = await createMutation.mutateAsync({
          userId: userId,
          address: data.address,
          city: data.city,
          zipCode: data.zipCode,
          stateId: data.stateId,
          countryId: data.countryId,
          bio: data.bio,
          profession: data.profession,
          dateOfBirth: data.dateOfBirth,
          nationality: data.nationality,
          socialLinks: data.socialLinks,
          educationLevel: data.educationLevel,
          leveling: data.leveling,
        });
        resultData = { ...data, personId: created.personId, userId };
      }

      onSubmit(resultData);
    } catch (error) {
      console.error("Error saving person details:", error);
      toast.error(t('common.toasts.actionError', { action: 'save', entity: 'person details' }));
    }
  };

  const labelMappings = useMemo(() => ({
    address: t('person.form.labels.address'),
    countryId: t('person.form.labels.country'),
    stateId: t('person.form.labels.state'),
    city: t('person.form.labels.city'),
    zipCode: t('person.form.labels.zipCode'),
    bio: t('person.form.labels.bio'),
    profession: t('person.form.labels.profession'),
    dateOfBirth: t('person.form.labels.birthDate'),
    nationality: t('person.form.labels.nationality'),
    socialLinks: t('person.form.labels.socialLinks'),
    educationLevel: t('person.form.labels.educationLevel'),
    leveling: t('person.form.labels.leveling'),
  }), [t]);

  const hiddenFields = ['personId', 'userId', 'status'];

  const foreignKeyOptions = {
    countries: countryOptions.map((country: any) => ({
      id: country.countryId,
      label: country.name,
    })),
    states: stateOptions.map((state: any) => ({
      id: state.stateId,
      label: state.name,
    })),
  };

  const customRenderers = useMemo(
    () => ({
      stateId: ({ field, methods }: StateRendererContext) => (
        <StateSelectField
          field={field}
          methods={methods}
          states={stateOptions}
          label={labelMappings.stateId}
        />
      ),
    }),
    [stateOptions, labelMappings]
  );

  return (
    <DynamicForm
      schema={personUpdateSchema}
      onSubmit={handleFormSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!personId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      hideButtons={hideButtons}
      formRef={formRef}
      foreignKeyOptions={foreignKeyOptions}
      customRenderers={customRenderers}
      onDirtyChange={onDirtyChange}
    />
  );
});
